"use client";

import { useEffect, useMemo, useState } from "react";
import Image from "next/image";
import type { Sponsor } from "@/server/sponsoren";

const PER_PAGINA = 4;
const INTERVAL_MS = 5000;

// Logo van de live site, met de naam als terugval wanneer het bestand ontbreekt.
function SponsorLogo({ sponsor }: { sponsor: Sponsor }) {
  const [fout, setFout] = useState(false);

  if (fout) {
    return (
      <span className="px-2 text-center font-display text-sm font-black uppercase leading-tight tracking-tight text-card">
        {sponsor.naam}
      </span>
    );
  }

  // Vaste hoogte met object-contain: de logo's hebben sterk uiteenlopende
  // verhoudingen, dus we reserveren de ruimte en laten Next het bestand
  // verkleinen (het Mitas-logo is onbewerkt 5,2 MB).
  return (
    <div className="relative h-12 w-full">
      <Image
        src={sponsor.logoUrl}
        alt={sponsor.naam}
        fill
        sizes="(max-width: 448px) 45vw, 200px"
        onError={() => setFout(true)}
        className="object-contain"
      />
    </div>
  );
}

function Tegel({ sponsor }: { sponsor: Sponsor }) {
  const klassen =
    "flex h-20 items-center justify-center rounded-xl border-2 border-primary bg-white p-3";

  return sponsor.website ? (
    <a
      href={sponsor.website}
      target="_blank"
      rel="noopener noreferrer"
      title={sponsor.naam}
      className={`${klassen} transition-opacity hover:opacity-80`}
    >
      <SponsorLogo sponsor={sponsor} />
    </a>
  ) : (
    <div className={klassen}>
      <SponsorLogo sponsor={sponsor} />
    </div>
  );
}

// Toont de sponsoren vier tegels per keer en rouleert door de rest. Staat stil
// bij één pagina, en respecteert de systeeminstelling voor minder beweging.
export function SponsorCarrousel({ sponsoren }: { sponsoren: Sponsor[] }) {
  const paginas = useMemo(() => {
    const uit: Sponsor[][] = [];
    for (let i = 0; i < sponsoren.length; i += PER_PAGINA) {
      uit.push(sponsoren.slice(i, i + PER_PAGINA));
    }
    return uit;
  }, [sponsoren]);

  const [actief, setActief] = useState(0);

  useEffect(() => {
    if (paginas.length <= 1) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const timer = setInterval(() => {
      setActief((i) => (i + 1) % paginas.length);
    }, INTERVAL_MS);
    return () => clearInterval(timer);
  }, [paginas.length]);

  if (sponsoren.length === 0) {
    return (
      <p className="px-4 py-6 text-center text-sm text-muted-foreground">
        Sponsoren worden binnenkort getoond.
      </p>
    );
  }

  const pagina = paginas[Math.min(actief, paginas.length - 1)];

  return (
    <div className="px-4">
      <div key={actief} className="grid animate-in grid-cols-2 gap-3 fade-in duration-700">
        {pagina.map((s) => (
          <Tegel key={s.id} sponsor={s} />
        ))}
      </div>

      {paginas.length > 1 && (
        <div className="mt-4 flex items-center justify-center gap-2">
          {paginas.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setActief(i)}
              aria-label={`Sponsoren, groep ${i + 1} van ${paginas.length}`}
              aria-current={i === actief ? "true" : undefined}
              className={
                i === actief
                  ? "h-2 w-5 rounded-full bg-primary transition-all"
                  : "size-2 rounded-full bg-white/25 transition-all hover:bg-white/40"
              }
            />
          ))}
        </div>
      )}
    </div>
  );
}
